import { Box, TextField, webFonts, webPalette } from '@munifocus/ui';
import { useId, type ReactNode, type Ref } from 'react';

// Shared form-field styling + primitives for the inquiry dialogs (generic
// contact + training session inquiries), so every form looks identical.

// The pure validation/format helpers now live in @munifocus/utils so the admin
// portal shares them; re-exported here to keep existing web call sites working.
export { formatPhoneInput, isValidPhone, isValidEmail } from '@munifocus/utils';

export const cleanInputSx = {
  fontFamily: webFonts.body,
  fontSize: 15,
};

export const fieldSx = {
  mt: 0.875,
  '& .MuiOutlinedInput-root': {
    backgroundColor: webPalette.white,
    borderRadius: '8px',
    '& fieldset': { borderColor: webPalette.line },
    '&:hover fieldset': { borderColor: webPalette.line },
    '&.Mui-focused fieldset': {
      borderColor: webPalette.teal,
      boxShadow: `0 0 0 3px ${webPalette.overlayTealFocusRing}`,
    },
  },
  '& .MuiOutlinedInput-input': { padding: '13px 14px' },
} as const;

/**
 * The uppercase field caption.
 *
 * Pass `htmlFor` whenever it captions a single control — it then renders a real
 * `<label>` bound to it. Without that the caption is just a nearby `div`, which
 * is what left every field in the contact and session dialogs programmatically
 * unnamed (WCAG 1.3.1 / 3.3.2 / 4.1.2). For a group of controls (a radio set)
 * give it an `id` instead and point the group's `aria-labelledby` at it.
 */
export function FieldLabel({
  children,
  htmlFor,
  id,
}: {
  children: ReactNode;
  htmlFor?: string;
  id?: string;
}) {
  return (
    <Box
      component={htmlFor ? 'label' : 'div'}
      htmlFor={htmlFor}
      id={id}
      sx={{
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: webPalette.ink,
        display: 'block',
      }}
    >
      {children}
    </Box>
  );
}

interface FieldInputProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  autoComplete?: string;
  multiline?: boolean;
  minRows?: number;
  /** Lets a form move focus here when this is the first invalid field on submit. */
  inputRef?: Ref<HTMLElement> | ((el: HTMLElement | null) => void);
  /** Shown under the field while it is valid (an example, a format). */
  hint?: ReactNode;
}

export function FieldInput({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  type,
  required,
  error,
  helperText,
  inputMode,
  autoComplete,
  multiline,
  minRows,
  inputRef,
  hint,
}: FieldInputProps) {
  // Binds the caption to the input. MUI derives `aria-describedby` for the
  // helper text from the same id, and sets `aria-invalid` from `error`, so the
  // validation message is announced as part of the field rather than as loose
  // red text beside it.
  const id = useId();
  return (
    <Box>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <TextField
        id={id}
        fullWidth
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        error={error}
        multiline={multiline}
        minRows={minRows}
        helperText={error ? helperText : hint}
        inputRef={inputRef}
        slotProps={{ input: { sx: cleanInputSx, inputMode, autoComplete } }}
        sx={fieldSx}
      />
    </Box>
  );
}
